import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import { api } from '../api';
import logger from '../utils/logger';

const MOBILE_BREAKPOINT = 700;

const formatDateTime = (value) => {
  if (!value) return 'Pending';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusMeta = (status) => {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'delivered') {
    return { icon: '✅', label: 'Delivered' };
  }

  if (normalized === 'shipped') {
    return { icon: '🚚', label: 'Shipped' };
  }

  if (normalized === 'packed' || normalized === 'confirmed') {
    return { icon: '📦', label: status };
  }

  if (normalized === 'cancelled') {
    return { icon: '❌', label: 'Cancelled' };
  }

  return { icon: '⏳', label: status || 'Pending' };
};

function ShipmentTracking() {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTracking = async () => {
      if (!orderId) {
        setError('Order ID missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/aastrog_cart/orders/${orderId}/tracking`);

        if (response.data?.success) {
          setTracking(response.data.data);
        } else {
          setError('Unable to load tracking details');
        }
      } catch (err) {
        logger.error('❌ fetchTracking error', err);
        setError(err.response?.data?.error || 'Unable to load tracking details');
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [orderId]);

  const progressPercent = useMemo(() => {
    const steps = tracking?.trackingSteps || [];
    if (!steps.length) return 0;
    const completedCount = steps.filter((step) => step.completed).length;
    return Math.max(0, Math.min(100, Math.round((completedCount / steps.length) * 100)));
  }, [tracking]);

  if (loading) {
    return (
      <div className="cart-container">
        <div className="loading-message">⏳ Loading order tracking...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cart-container">
        <div className="error-message">⚠️ {error}</div>
        <button className="continue-shopping-btn" onClick={() => navigate('/orders')}>
          Back to Orders
        </button>
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="cart-container">
        <div className="empty-cart">
          <h3>Tracking details not available</h3>
          <button className="continue-shopping-btn" onClick={() => navigate('/orders')}>
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const currentMeta = getStatusMeta(tracking.currentStep);

  return (
    <div className="cart-container shipment-tracking-page">
      <div className="cart-header">
        <button className="back-button product-details-back-button" onClick={() => navigate('/orders')}>
          <MdArrowBack size={isMobile ? 16 : 24} />
          {!isMobile && 'Back'}
        </button>
        <h1 className="cart-title">Order Tracking</h1>
      </div>

      <div className="shipment-top-card">
        <div className="shipment-top-main shipment-info-grid shipment-info-grid--top">
          <div className="shipment-info-card shipment-order-block">
            <div className="shipment-label">Order Number</div>
            <div className="shipment-value">#{tracking.orderNumber}</div>
            <div className="shipment-badge">
              {currentMeta.icon} {currentMeta.label}
            </div>
          </div>

          <div className="shipment-info-card">
            <div className="shipment-label">Payment</div>
            <div className="shipment-value-small">{tracking.paymentStatus || 'N/A'}</div>
            <div className="shipment-label shipment-sub-label">Estimated Delivery</div>
            <div className="shipment-value-small">{formatDateTime(tracking.estimatedDelivery)}</div>
          </div>

          <div className="shipment-info-card">
            <div className="shipment-label">Order Date</div>
            <div className="shipment-value-small">{formatDateTime(tracking.createdAt)}</div>
          </div>

          <div className="shipment-info-card shipment-progress-card">
            <div className="shipment-progress-meta">
              <span>Shipment Progress</span>
              <strong>{progressPercent}%</strong>
            </div>
            <div className="shipment-progress-bar">
              <div className="shipment-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="shipment-timeline-card">
        <h3 className="shipment-section-title">Shipment Timeline</h3>

        <div className="shipment-timeline-list">
          {(tracking.trackingSteps || []).map((step) => (
            <div
              key={step.status}
              className={`shipment-timeline-item ${step.completed ? 'completed' : ''} ${step.active ? 'active' : ''}`}
            >
              <div className="shipment-timeline-dot">{step.completed ? '✓' : ''}</div>
              <div className="shipment-timeline-content">
                <div className="shipment-step-title">{step.status}</div>
                <div className="shipment-step-time">{formatDateTime(step.timestamp)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="shipment-actions">
        <button className="continue-shopping-btn" onClick={() => navigate(`/order-confirmation/${tracking.orderId}`)}>
          View Order Details
        </button>
        <button className="checkout-btn" onClick={() => navigate('/products')}>
          Continue Shopping
        </button>
      </div>
    </div>
  );
}

export default ShipmentTracking;
